import { useState } from 'react';
import type { IngestBrief, MemoryNote, Watch } from '../lib/assistant.js';
import type { SavedReport } from '../lib/reports.js';

/** Everything the assistant remembers — its own tab, so a busy week of ingests
 *  (or a bulk backfill) never buries the chat. */
export function MemoryPanel({
  memory,
  onAddMemory,
  onRemoveMemory,
  watches,
  onDeleteWatch,
  saved,
  onRun,
  onDelete,
  onSchedule,
  briefs,
}: {
  memory: MemoryNote[];
  onAddMemory: (note: string) => void;
  onRemoveMemory: (n: MemoryNote) => void;
  watches: Watch[];
  onDeleteWatch: (w: Watch) => void;
  saved: SavedReport[];
  onRun: (r: SavedReport) => void;
  onDelete: (r: SavedReport) => void;
  onSchedule: (r: SavedReport, cadence: 'off' | 'weekly' | 'monthly') => void;
  briefs: IngestBrief[];
}) {
  const [newNote, setNewNote] = useState('');
  return (
    <div className="mx-auto w-full max-w-2xl space-y-8 text-left">
      <div>
        <p className="mb-2 text-sm font-semibold text-neutral-600 dark:text-neutral-300">
          What the assistant knows{' '}
          <span className="font-normal text-neutral-400">
            — facts it reads before answering; add anything worth remembering
          </span>
        </p>
        {memory.length > 0 && (
          <ul className="mb-2 space-y-1">
            {memory.map((n) => (
              <li
                key={n.id}
                className="flex items-start justify-between gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
              >
                <span>
                  {n.note}
                  {n.source !== 'customer' && (
                    <span className="ml-1.5 text-[10px] uppercase tracking-wide text-neutral-400">
                      {n.source === 'admin' ? 'CommissionSight' : 'auto'}
                    </span>
                  )}
                </span>
                {n.editable && (
                  <button
                    type="button"
                    onClick={() => onRemoveMemory(n)}
                    className="shrink-0 px-1 text-neutral-400 transition hover:text-rose-500"
                    aria-label="Remove this note"
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newNote.trim().length >= 3) {
                onAddMemory(newNote.trim());
                setNewNote('');
              }
            }}
            maxLength={300}
            placeholder="e.g. “Our Aetna book moved to a new agency in May 2026”"
            className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-emerald-500 dark:border-neutral-800 dark:bg-neutral-900"
          />
          <button
            type="button"
            disabled={newNote.trim().length < 3}
            onClick={() => {
              onAddMemory(newNote.trim());
              setNewNote('');
            }}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            Remember
          </button>
        </div>
      </div>

      {watches.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-neutral-600 dark:text-neutral-300">
            Watching{' '}
            <span className="font-normal text-neutral-400">
              — re-checked after every statement; you get an email if the count grows
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {watches.map((w) => (
              <span
                key={w.id}
                className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white pl-3 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
                title={`Currently matching: ${w.lastCount}`}
              >
                <span className="py-1">
                  ⏱ {w.question.length > 60 ? `${w.question.slice(0, 60)}…` : w.question}
                  <span className="ml-1.5 text-xs text-neutral-400">({w.lastCount})</span>
                </span>
                <button
                  type="button"
                  onClick={() => onDeleteWatch(w)}
                  className="px-1.5 text-neutral-400 transition hover:text-rose-500"
                  aria-label={`Stop watching: ${w.question}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {saved.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-neutral-600 dark:text-neutral-300">
            Saved reports <span className="font-normal text-neutral-400">— click to re-run</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {saved.map((r) => (
              <span
                key={r.id}
                className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white pl-3 text-sm text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
              >
                <button
                  type="button"
                  onClick={() => onRun(r)}
                  className="py-1 font-medium transition hover:text-emerald-700 dark:hover:text-emerald-400"
                  title={r.question ?? r.name}
                >
                  {r.name}
                </button>
                <select
                  value={r.cadence ?? 'off'}
                  onChange={(e) => onSchedule(r, e.target.value as 'off' | 'weekly' | 'monthly')}
                  className="border-none bg-transparent py-1 text-xs text-neutral-500 outline-none focus:text-neutral-700 dark:text-neutral-400 dark:focus:text-neutral-200"
                  title="Email this report on a schedule"
                  aria-label={`Email schedule for ${r.name}`}
                >
                  <option value="off">✉ no email</option>
                  <option value="weekly">✉ email weekly</option>
                  <option value="monthly">✉ email monthly</option>
                </select>
                <button
                  type="button"
                  onClick={() => onDelete(r)}
                  className="px-1.5 text-neutral-400 transition hover:text-rose-500"
                  aria-label={`Delete ${r.name}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {briefs.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-neutral-600 dark:text-neutral-300">
            Recent statement briefs{' '}
            <span className="font-normal text-neutral-400">
              — the assistant’s summary of each processed statement
            </span>
          </p>
          <ul className="space-y-2">
            {briefs.map((b) => (
              <li
                key={b.id}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 dark:border-neutral-800 dark:bg-neutral-900"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-400">
                  {b.carrierName ?? 'carrier'} · {b.period}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-neutral-700 dark:text-neutral-300">
                  {b.brief}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
